import { createContext, useContext, useCallback, useMemo, useRef, useState } from 'react';
import type { Dispatch, SetStateAction } from 'react';
import type { WebMapLayerConfig } from '../types/webMap';
import type { ColumnInfo } from '@signals/app-shell/services/mapLayersService';
import { timeWindowCutoff, TIMESTAMP_FIELDS, type TimeWindowOption } from '../builder/dashboardTimeWindowOptions';
import type { LocalGeofence } from '../builder/types';
import { filterFeaturesByCrossFilters } from './crossFilterUtils';

export interface MapExtent {
  north: number;
  south: number;
  east: number;
  west: number;
}

export interface CrossFilter {
  field: string;
  value: unknown;
  source: string;
  /** When set, apply this filter only to the specified Web Map layer. */
  layerId?: string;
}

export interface WebMapContextValue {
  layers: WebMapLayerConfig[];
  layerSchemas: Map<string, ColumnInfo[]>;
  layerFeatures: Map<string, GeoJSON.Feature[]>;

  extent: MapExtent | null;
  filterByExtent: boolean;
  setFilterByExtent: (v: boolean) => void;
  selectedFeatures: GeoJSON.Feature[];

  timeWindow: TimeWindowOption;
  setTimeWindow: (tw: TimeWindowOption) => void;

  crossFilters: Map<string, CrossFilter>;
  setCrossFilter: (key: string, filter: CrossFilter) => void;
  removeCrossFilter: (key: string) => void;
  clearCrossFilters: () => void;

  activeFilters: Map<string, Map<string, unknown>>;
  applyFilter: (layerId: string, filterName: string, expression: unknown) => void;
  removeFilter: (layerId: string, filterName: string) => void;

  getFeaturesForLayer: (layerId: string) => GeoJSON.Feature[];
  /** Like getFeaturesForLayer but omits extent filter — for map rendering without feedback loop. */
  getFilteredFeaturesForMap: (layerId: string) => GeoJSON.Feature[];
  getFieldValues: (layerId: string, field: string) => unknown[];
  aggregate: (layerId: string, field: string, op: 'count' | 'sum' | 'avg' | 'min' | 'max') => number;

  setLayers: (layers: WebMapLayerConfig[]) => void;
  /** Accepts either a Map or an updater (prev) => next — use updater form for concurrent per-layer writes. */
  setLayerSchemas: Dispatch<SetStateAction<Map<string, ColumnInfo[]>>>;
  /** Accepts either a Map or an updater (prev) => next — use updater form for concurrent per-layer writes. */
  setLayerFeatures: Dispatch<SetStateAction<Map<string, GeoJSON.Feature[]>>>;
  setExtent: (ext: MapExtent | null) => void;
  setSelectedFeatures: (features: GeoJSON.Feature[]) => void;

  /** Geofence overlay — rendered by the web map widget, managed by the geofence widget. */
  geofenceOverlay: LocalGeofence[];
  setGeofenceOverlay: (fences: LocalGeofence[]) => void;
  geofenceSelectedId: string | null;
  setGeofenceSelectedId: (id: string | null) => void;
  /** When true, the web map enables polygon draw mode and calls onGeofenceDrawComplete when done. */
  geofenceDrawMode: boolean;
  setGeofenceDrawMode: (v: boolean) => void;
  onGeofenceDrawComplete: React.MutableRefObject<((geometry: { type: 'Polygon'; coordinates: number[][][] }) => void) | null>;

  /** Fly the map to a coordinate. Registered by WebMapWidget. */
  flyTo: (lng: number, lat: number, zoom?: number) => void;
  /** Briefly flash a point on the map (pulse animation). */
  flashFeature: (lng: number, lat: number) => void;
  /** Register the map's flyTo/flash implementations (called by WebMapWidget). */
  registerMapActions: (actions: { flyTo: (lng: number, lat: number, zoom?: number) => void; flash: (lng: number, lat: number) => void }) => void;
}

const WebMapContext = createContext<WebMapContextValue | null>(null);

export function useWebMapContext(): WebMapContextValue | null {
  return useContext(WebMapContext);
}

export function useWebMapProvider(initialLayers: WebMapLayerConfig[] = []): WebMapContextValue {
  const [layers, setLayers] = useState<WebMapLayerConfig[]>(initialLayers);
  const [layerSchemas, setLayerSchemas] = useState<Map<string, ColumnInfo[]>>(new Map());
  const [layerFeatures, setLayerFeatures] = useState<Map<string, GeoJSON.Feature[]>>(new Map());
  const [extent, setExtent] = useState<MapExtent | null>(null);
  const [filterByExtent, setFilterByExtent] = useState(false);
  const [selectedFeatures, setSelectedFeatures] = useState<GeoJSON.Feature[]>([]);
  const [activeFilters, setActiveFilters] = useState<Map<string, Map<string, unknown>>>(new Map());
  const [crossFilters, setCrossFiltersState] = useState<Map<string, CrossFilter>>(new Map());
  const [timeWindow, setTimeWindow] = useState<TimeWindowOption>('All Time');

  const [geofenceOverlay, setGeofenceOverlay] = useState<LocalGeofence[]>([]);
  const [geofenceSelectedId, setGeofenceSelectedId] = useState<string | null>(null);
  const [geofenceDrawMode, setGeofenceDrawMode] = useState(false);
  const onGeofenceDrawComplete = useRef<((geometry: { type: 'Polygon'; coordinates: number[][][] }) => void) | null>(null);

  const mapActionsRef = useRef<{ flyTo: (lng: number, lat: number, zoom?: number) => void; flash: (lng: number, lat: number) => void } | null>(null);

  const flyTo = useCallback((lng: number, lat: number, zoom?: number) => {
    mapActionsRef.current?.flyTo(lng, lat, zoom);
  }, []);

  const flashFeature = useCallback((lng: number, lat: number) => {
    mapActionsRef.current?.flash(lng, lat);
  }, []);

  const registerMapActions = useCallback((actions: { flyTo: (lng: number, lat: number, zoom?: number) => void; flash: (lng: number, lat: number) => void }) => {
    mapActionsRef.current = actions;
  }, []);

  const applyFilter = useCallback((layerId: string, filterName: string, expression: unknown) => {
    setActiveFilters((prev) => {
      const next = new Map<string, Map<string, unknown>>(prev);
      const layerFilters = new Map<string, unknown>(next.get(layerId) ?? []);
      layerFilters.set(filterName, expression);
      next.set(layerId, layerFilters);
      return next;
    });
  }, []);

  const removeFilter = useCallback((layerId: string, filterName: string) => {
    setActiveFilters((prev) => {
      const next = new Map<string, Map<string, unknown>>(prev);
      const layerFilters = next.get(layerId);
      if (layerFilters) {
        const updated = new Map(layerFilters);
        updated.delete(filterName);
        if (updated.size === 0) next.delete(layerId);
        else next.set(layerId, updated);
      }
      return next;
    });
  }, []);

  const setCrossFilter = useCallback((key: string, filter: CrossFilter) => {
    setCrossFiltersState((prev) => {
      const next = new Map(prev);
      next.set(key, filter);
      return next;
    });
  }, []);

  const removeCrossFilter = useCallback((key: string) => {
    setCrossFiltersState((prev) => {
      const next = new Map(prev);
      next.delete(key);
      return next;
    });
  }, []);

  const clearCrossFilters = useCallback(() => {
    setCrossFiltersState(new Map());
  }, []);

  const getFeaturesInExtent = useCallback(
    (features: GeoJSON.Feature[]): GeoJSON.Feature[] => {
      if (!filterByExtent || !extent) return features;
      return features.filter((f) => {
        const geom = f.geometry;
        if (!geom) return false;
        if (geom.type === 'Point') {
          const [lng, lat] = geom.coordinates;
          return lng >= extent.west && lng <= extent.east && lat >= extent.south && lat <= extent.north;
        }
        return true;
      });
    },
    [extent, filterByExtent]
  );

  const applyTimeFilter = useCallback(
    (features: GeoJSON.Feature[]): GeoJSON.Feature[] => {
      const cutoff = timeWindowCutoff(timeWindow);
      if (!cutoff) return features;
      return features.filter((f) => {
        const props = f.properties;
        if (!props) return false;
        for (const tf of TIMESTAMP_FIELDS) {
          const raw = props[tf];
          if (raw != null) {
            const d = new Date(raw as string | number);
            return !isNaN(d.getTime()) && d >= cutoff;
          }
        }
        return true;
      });
    },
    [timeWindow]
  );

  const applyCrossFilters = useCallback(
    (features: GeoJSON.Feature[], layerId: string): GeoJSON.Feature[] => {
      return filterFeaturesByCrossFilters(features, crossFilters.values(), layerId);
    },
    [crossFilters]
  );

  const getFeaturesForLayer = useCallback(
    (layerId: string): GeoJSON.Feature[] => {
      const raw = layerFeatures.get(layerId) ?? [];
      let result = getFeaturesInExtent(raw);
      result = applyTimeFilter(result);
      result = applyCrossFilters(result, layerId);
      return result;
    },
    [layerFeatures, getFeaturesInExtent, applyTimeFilter, applyCrossFilters]
  );

  const getFilteredFeaturesForMap = useCallback(
    (layerId: string): GeoJSON.Feature[] => {
      const raw = layerFeatures.get(layerId) ?? [];
      let result = applyTimeFilter(raw);
      result = applyCrossFilters(result, layerId);
      return result;
    },
    [layerFeatures, applyTimeFilter, applyCrossFilters]
  );

  const getFieldValues = useCallback(
    (layerId: string, field: string): unknown[] => {
      return getFeaturesForLayer(layerId)
        .map((f) => f.properties?.[field])
        .filter((v) => v != null);
    },
    [getFeaturesForLayer]
  );

  const aggregate = useCallback(
    (layerId: string, field: string, op: 'count' | 'sum' | 'avg' | 'min' | 'max'): number => {
      const values = getFieldValues(layerId, field);
      if (op === 'count') return values.length;
      const nums = values.map(Number).filter(Number.isFinite);
      if (nums.length === 0) return 0;
      switch (op) {
        case 'sum':
          return nums.reduce((a, b) => a + b, 0);
        case 'avg':
          return nums.reduce((a, b) => a + b, 0) / nums.length;
        case 'min':
          return Math.min(...nums);
        case 'max':
          return Math.max(...nums);
        default:
          return 0;
      }
    },
    [getFieldValues]
  );

  return useMemo(
    () => ({
      layers,
      layerSchemas,
      layerFeatures,
      extent,
      filterByExtent,
      setFilterByExtent,
      selectedFeatures,
      timeWindow,
      setTimeWindow,
      crossFilters,
      setCrossFilter,
      removeCrossFilter,
      clearCrossFilters,
      activeFilters,
      applyFilter,
      removeFilter,
      getFeaturesForLayer,
      getFilteredFeaturesForMap,
      getFieldValues,
      aggregate,
      setLayers,
      setLayerSchemas,
      setLayerFeatures,
      setExtent,
      setSelectedFeatures,
      geofenceOverlay,
      setGeofenceOverlay,
      geofenceSelectedId,
      setGeofenceSelectedId,
      geofenceDrawMode,
      setGeofenceDrawMode,
      onGeofenceDrawComplete,
      flyTo,
      flashFeature,
      registerMapActions,
    }),
    [layers, layerSchemas, layerFeatures, extent, filterByExtent, selectedFeatures, timeWindow, crossFilters, activeFilters, applyFilter, removeFilter, setCrossFilter, removeCrossFilter, clearCrossFilters, getFeaturesForLayer, getFilteredFeaturesForMap, getFieldValues, aggregate, geofenceOverlay, geofenceSelectedId, geofenceDrawMode, flyTo, flashFeature, registerMapActions]
  );
}

export const WebMapProvider = WebMapContext.Provider;

export default WebMapContext;
